/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import { StylesConfig } from "react-select";
import styled from "styled-components";
import { Text } from "../Text/Text.js";
import { fontSizeMap as textFontSizeMap } from "../Text/Text.style.js";
import { rem } from "../Theme/Theme.constants.js";
import { ITheme } from "../Theme/Theme.types.js";
import { SelectProps } from "./Select.types.js";

const BASE_HORIZONTAL_PADDING_REM = 1;

const getOptionBackgroundColor = (params: { isFocused: boolean; isSelected: boolean; theme: ITheme }): string => {
    const { isFocused, isSelected, theme } = params;
    if (isSelected) return theme.colors.primary[100];
    if (isFocused) return theme.grayscale[10];
    return theme.grayscale.white;
};

const getOptionColor = (params: { isFocused: boolean; isSelected: boolean; theme: ITheme }): string => {
    const { isFocused, isSelected, theme } = params;
    if (isSelected) return "white";
    if (isFocused) return theme.grayscale[100];
    return theme.grayscale[100];
};

const getSize = (size: NonNullable<SelectProps["size"]> | undefined): string => {
    switch (size) {
        case "large":
            return rem(3);
        case "medium":
            return rem(2.7);
        case "small":
            return rem(2);
        default:
            return rem(2);
    }
};

const fontSizeMap: Record<NonNullable<SelectProps["size"]>, number> = {
    large: textFontSizeMap.small,
    medium: textFontSizeMap["x-small"],
    small: textFontSizeMap["xx-small"],
};

const getPlaceholderTextColor = ({
    theme,
    variant,
    isDisabled,
}: {
    theme: ITheme;
    variant: NonNullable<SelectProps["variant"]>;
    isDisabled: boolean;
}): string => {
    if (isDisabled) return theme.grayscale[50];
    if (variant === "primary") return theme.colors.primary[100];
    return theme.grayscale[70];
};

export const selectStyles = (
    theme: ITheme,
    asAutocomplete: NonNullable<SelectProps["asAutocomplete"]>,
    variant: NonNullable<SelectProps["variant"]>,
    inError: NonNullable<SelectProps["inError"]>,
    size?: NonNullable<SelectProps["size"]>,
): StylesConfig => ({
    placeholder: (provided, { isDisabled }) => ({
        ...provided,
        color: getPlaceholderTextColor({ theme, variant, isDisabled }),
        WebkitFontSmoothing: "antialiased",
        fontSize: theme.rem(fontSizeMap[size || "medium"]),
        fontWeight: 500,
        margin: 0,
    }),
    control: (provided, { isDisabled }) => ({
        ...provided,
        backgroundColor: isDisabled ? theme.grayscale[10] : variant === "primary" ? theme.colors.primary[5] : theme.grayscale.whiteGlass,
        border: `1.5px solid ${
            inError
                ? theme.colors.error[100]
                : isDisabled
                  ? theme.grayscale[30]
                  : variant === "flat"
                    ? theme.grayscale[50]
                    : variant === "primary"
                      ? theme.colors.primary[5]
                      : theme.grayscale.white
        }`,
        boxShadow: "none",
        minHeight: "26px",
        height: getSize(size),
        borderRadius: size === "small" ? theme.radius.xs : theme.radius.s,
        cursor: asAutocomplete ? "text" : "pointer",

        "&:hover": {
            ...(inError ? {} : { borderColor: theme.colors.primary[100] }),
        },
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        div: {
            padding: `0 ${theme.spacing[8]}`,
        },
    }),
    input: (provided) => ({
        ...provided,
        color: variant === "primary" ? theme.colors.primary[100] : theme.grayscale[100],
        input: {
            opacity: "1 !important",
        },
        margin: "0 !important",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: variant === "primary" ? theme.colors.primary[100] : theme.grayscale[100],
        fontSize: theme.rem(fontSizeMap[size || "medium"]),
        WebkitFontSmoothing: "antialiased",
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
    }),
    dropdownIndicator: (provided, { hasValue, selectProps }) => ({
        ...provided,
        color: variant === "primary" ? theme.colors.primary[100] : hasValue ? theme.grayscale[100] : theme.grayscale[80],
        transform: selectProps.menuIsOpen ? "rotate(-180deg)" : "",
        transition: "all .2s ease",
    }),
    menu: (provided) => ({
        ...provided,
        boxShadow: theme.shadows.popover,
        zIndex: 99,
        backgroundColor: theme.grayscale.white,
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: `0 ${theme.rem(BASE_HORIZONTAL_PADDING_REM)}`,
    }),

    option: (provided, { isFocused, isSelected }) => ({
        ...provided,
        backgroundColor: getOptionBackgroundColor({ theme, isSelected, isFocused }),
        cursor: "pointer",
        WebkitFontSmoothing: "antialiased",
        fontSize: theme.rem(fontSizeMap[size || "medium"]),
        color: getOptionColor({ theme, isSelected, isFocused }),
        "&:hover": {
            color: getOptionColor({ theme, isSelected, isFocused: true }),
            backgroundColor: getOptionBackgroundColor({
                theme,
                isSelected,
                isFocused: true,
            }),
        },
    }),
});

export const ValueContainerContent = styled.div`
    display: flex;
    align-items: center;
    gap: ${(props) => props.theme.spacing[8]};
`;

export const StyledIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.grayscale[80]};

    & svg {
        width: ${({ theme }) => theme.rem(1)};
        height: ${({ theme }) => theme.rem(1)};
    }
`;

export const StyledIconCreate = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: ${(props) => `${props.theme.spacing[8]}`};

    & svg {
        width: ${({ theme }) => theme.rem(1)};
        height: ${({ theme }) => theme.rem(1)};
    }
`;
export const StyleCreateDiv = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

export const StyledText = styled(Text)``;

export const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${(props) => `${props.theme.spacing[2]}`};
`;
